import React, { useState } from 'react';

import { SnackbarKey, useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Button, CircularProgress, IconButton, Paper, Typography } from '@mui/material';

import SearchService from '~/services/SearchService';

import RestoreDialog from './RestoreDialog';
import { RestoreButtonProps } from './types';

const RESTORE_STATUS_WAIT_MS = 30_000;
const RESTORE_ONGOING_MESSAGE = 'Restore is ongoing, check with search for status updates';
const TIMEOUT_STATUSES = new Set([408, 504, 524]);

type RestoreOutcome = 'success' | 'timeout' | 'error';

function isRestoreTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const name = 'name' in error ? String(error.name) : '';
  if (name === 'AbortError' || name === 'TimeoutError') {
    return true;
  }

  const status = 'status' in error ? Number(error.status) : undefined;
  if (status !== undefined && TIMEOUT_STATUSES.has(status)) {
    return true;
  }

  const message = 'message' in error ? String(error.message).toLowerCase() : '';
  return message.includes('timeout') || message.includes('timed out');
}

function RestoreButton({ restoreType, title, description, timeRange }: RestoreButtonProps) {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const dismissAction = (key: SnackbarKey) => (
    <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(key)}>
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleConfirm = async () => {
    setDialogOpen(false);
    setLoading(true);

    // Keep the request alive after the wait so a slow restore is not cancelled.
    const restoreRequest: Promise<RestoreOutcome> = SearchService.restoreSearch(restoreType)
      .then(() => 'success' as const)
      .catch((error: unknown) => (isRestoreTimeoutError(error) ? 'timeout' : 'error'));

    let timeoutId = 0;
    const waitLimit = new Promise<RestoreOutcome>((resolve) => {
      timeoutId = window.setTimeout(() => resolve('timeout'), RESTORE_STATUS_WAIT_MS);
    });

    try {
      const outcome = await Promise.race([restoreRequest, waitLimit]);

      if (outcome === 'success') {
        enqueueSnackbar(`Restore started for ${title}`, {
          variant: 'success',
          persist: true,
          action: dismissAction,
        });
        return;
      }

      if (outcome === 'timeout') {
        enqueueSnackbar(RESTORE_ONGOING_MESSAGE, {
          variant: 'info',
          persist: true,
          action: dismissAction,
        });
        return;
      }

      enqueueSnackbar(`Failed to restore ${title}`, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      window.clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '3px solid',
          borderColor: 'info.main',
          '&:hover': {
            elevation: 3,
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          {title}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2, mb: 1.5, flexGrow: 1 }}>
          {description.map((point, index) => (
            <Typography
              component="li"
              variant="body2"
              key={index}
              sx={{
                fontSize: '0.875rem',
                '&:not(:last-child)': { mb: 0.5 },
              }}
            >
              {point}
            </Typography>
          ))}
        </Box>

        <Button
          variant="outlined"
          color="info"
          onClick={handleClick}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UpdateIcon />}
          size="small"
          fullWidth
        >
          {loading ? 'Restoring...' : 'Restore'}
        </Button>
      </Paper>

      <RestoreDialog
        open={dialogOpen}
        restoreType={restoreType}
        timeRange={timeRange}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default RestoreButton;
